import { Divider } from '@dixa/lib-shared-habla';
import { ampli } from 'lib-shared-tracking';
import { Channel } from 'lib-shared-types-channels';
import { useCallback, useMemo } from 'react';

import { CommandKeybindingsTooltipContent } from 'components/commandKeybindingsTooltipContent';
import {
  copilotActionRegistry,
  defaultPinnedState,
  useCopilotActionRunner,
  type CopilotActionContext,
  type CopilotActionId,
  type CopilotRunOptions,
} from 'components/copilotActions';
import { copilotDropdownFeatureFlags } from 'components/copilotDropdown/copilotDropdown.constants';
import { useFeatureFlag } from 'utils/featureFlagging/useFeatureFlag';
import { useCopilotTrial } from 'utils/useCopilotTrial';
import { useServerStorage } from 'utils/useServerStorage';

import { PinnedActionButton } from './components/pinnedActionButton';
import { PinnedImproveReply } from './components/pinnedImproveReply';
import { Undo } from './components/undo';
import styles from './copilotPinnedActions.module.scss';
import { usePinnedActionsCollapse } from './usePinnedActionsCollapse';

export type CopilotPinnedActionsProps = {
  /** Which actions this editor offers, and any wording overrides. */
  context: CopilotActionContext;
  /** Live editor text, used for disabled state. */
  text: string;
  channel?: Channel;
  csid?: string;
  languageCode?: string;
};

export const CopilotPinnedActions = ({
  context,
  text,
  channel,
  csid,
  languageCode,
}: CopilotPinnedActionsProps): JSX.Element => {
  const storage = useServerStorage('user');
  const { hasCopilot } = useCopilotTrial();
  const flags = useFeatureFlag(copilotDropdownFeatureFlags);

  const pinnedIds = useMemo(() => {
    const pinState = {
      // Default values
      ...defaultPinnedState,
      // User storage values override defaults
      ...storage.data.agent_copilot?.pinned,
    };

    if (!hasCopilot) {
      return [];
    }

    return context.actions.filter((id) => {
      if (!pinState[id]) {
        return false;
      }

      const { featureFlag } = copilotActionRegistry[id];
      return !featureFlag || !!flags[featureFlag];
    });
  }, [context.actions, flags, hasCopilot, storage.data.agent_copilot?.pinned]);

  const { containerRef, collapsedIds } = usePinnedActionsCollapse(pinnedIds);

  const { run, getStatus, resolveName, improvements } = useCopilotActionRunner({
    context,
    text,
    channel,
    csid,
    languageCode,
  });

  const handleRun = useCallback(
    (id: CopilotActionId, options?: CopilotRunOptions) => {
      ampli.agentCopilotDropdownItemClicked({ key: `pinned:${id}`, csid });
      run(id, options);
    },
    [csid, run],
  );

  return (
    <>
      {pinnedIds.length > 0 && (
        <div className={styles.shrinkable} ref={containerRef}>
          <Divider orientation="vertical" className={styles.pinnedDivider} />
          {pinnedIds.map((id) => {
            const { icon, shortName, commandName } = copilotActionRegistry[id];
            const status = getStatus(id);
            const collapsed = collapsedIds.has(id);
            const name = resolveName(id);

            // tooltipContent doubles as the action's own label when it is ready,
            // so it is only a reason while disabled and saying something the
            // name does not.
            const reason =
              status?.isDisabled && status.tooltipContent !== name
                ? status.tooltipContent
                : undefined;

            // Each line is its own element, so the column lays them out as two
            // flex items instead of one run of text.
            const tooltip = (
              <CommandKeybindingsTooltipContent commandName={commandName}>
                <span className={styles.tooltipName}>{name}</span>
                {reason && <span>{reason}</span>}
              </CommandKeybindingsTooltipContent>
            );

            if (id === 'improveReply') {
              return (
                <PinnedImproveReply
                  key={id}
                  icon={icon}
                  label={shortName}
                  tooltip={tooltip}
                  collapsed={collapsed}
                  disabled={status?.isDisabled}
                  improvements={improvements}
                  onSelect={(improvement) =>
                    handleRun(id, {
                      promptName: improvement.value,
                      includeContext: improvement.includeContext,
                    })
                  }
                />
              );
            }

            return (
              <PinnedActionButton
                key={id}
                icon={icon}
                label={shortName}
                tooltip={tooltip}
                collapsed={collapsed}
                disabled={status?.isDisabled}
                onClick={() => handleRun(id)}
              />
            );
          })}
        </div>
      )}
      <Undo />
    </>
  );
};
